import React, { useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import { MdClose } from "react-icons/md";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import Box from "@mui/material/Box";
import classes from "./myteam.module.css";
import {
  authApi,
  getAuthorizationHeader,
} from "../../../api-calls/user-requests";
import { ADD_DEPARTMENT } from "../../../api-calls/api-urls";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { CirclePicker, ChromePicker } from "react-color";
import { getLanguagedataFunction } from "../../../utilities/language";
import AlertBox from "../../../components/AlertBox";

// Same 12-colour palette teamService.js's PALETTE (utils/departmentColors.js)
// and both mobile department screens use — CirclePicker's own built-in
// default palette is an unrelated 18-colour set from react-color, which
// meant departments created here almost never actually collided with
// ensureUniqueColour's exact-hex check, and showed different swatches than
// every other department-creation surface in the app.
const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#06B6D4", "#EC4899", "#6366F1",
  "#F97316", "#14B8A6", "#84CC16", "#F43F5E",
];

const AddDepartmentModal = (props) => {
  const dispatch = useDispatch();
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState("");
  const [openColourPalete, setOpenColourPalete] = useState(false);
  const [modalToggle, setModalToggle] = useState(true);
  const [success, setSuccess] = useState();
  const rootRef = React.useRef(null);
  // Seeded as controlled values from the start (rather than useState()'s
  // undefined) — TextValidator/MUI otherwise warns about a field switching
  // from uncontrolled to controlled the moment the user types.
  const [department, setDepartment] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const ln = useSelector((state) => state.allUsers.language_json);
  const departmentList = useSelector((state) => state.teams.departmentList);
  const closeTimeoutRef = React.useRef(null);

  // Colours already claimed by another department — excludes the
  // department being edited itself, so its own current colour isn't
  // filtered out of the palette.
  const usedColours = new Set(
    (departmentList || [])
      .filter((d) => d._id !== props.department?._id)
      .map((d) => d.colour)
      .filter(Boolean)
  );
  const availablePalette = PALETTE.filter((c) => !usedColours.has(c));

  React.useEffect(() => {
    getLanguagedataFunction(dispatch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount (getLanguagedataFunction is idempotent)

  const handleColour = (color) => {
    setSelectedColor(color.hex);
  };

  function closeModal(data) {
    props.modalClose(false, data);
    setModalToggle(false);
  }

  useEffect(() => {
    if (showAlert) {
      const timeout = setTimeout(() => {
        setShowAlert(false);
      }, 3000); // Change the duration (in milliseconds) as per your requirement

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [showAlert]);

  useEffect(() => {
    if (props.department) {
      setDepartment(props.department.department_name);
      setSelectedColor(props.department.colour);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default a new department's colour to the first one not already taken,
  // rather than leaving the picker unselected (or, previously, defaulting
  // to whatever CirclePicker's own default happened to be) — runs once the
  // department list has loaded. Editing is unaffected: the effect above
  // already seeds selectedColor from the department being edited.
  useEffect(() => {
    if (props.department || selectedColor || !departmentList) return;
    if (availablePalette.length) {
      setSelectedColor(availablePalette[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentList]);

  // Clear the pending auto-close timeout (set in handleSubmit below) if the
  // component unmounts first — otherwise it fires closeModal() on an
  // unmounted component.
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async () => {
    // ValidatorForm only validates the department-name TextValidator above —
    // the colour picker is a custom control with no wired-in "required"
    // check, so an unpicked colour used to submit as `colour: undefined`
    // straight into a schema field that requires it, surfacing as a raw
    // Mongoose validation message instead of a clear client-side one.
    if (!selectedColor) {
      setError("Please choose a department colour");
      setShowAlert(true);
      return;
    }

    try {
      const userEntry = {
        department_name: department,
        colour: selectedColor,
      };
      if (props.department) {
        await authApi.put(
          `${ADD_DEPARTMENT}/${props.department._id}`,
          userEntry,
          {
            headers: { Authorization: getAuthorizationHeader() },
          }
        );
      } else {
        await authApi.post(ADD_DEPARTMENT, userEntry, {
          headers: { Authorization: getAuthorizationHeader() },
        });
      }
      setShowAlert(true);
      setSuccess(
        props.department
          ? "Department updated successfully."
          : "Department created successfully."
      );
      closeTimeoutRef.current = setTimeout(() => {
        closeModal();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
      setShowAlert(true);
    }
  };

  return (
    <div>
      {ln ? (
        <Modal
          open={modalToggle}
          onClose={() => closeModal()}
          aria-labelledby="server-modal-title"
          data-testid="register_modal"
          aria-describedby="server-modal-description"
          sx={{
            display: "flex",
            marginTop: 10,
            justifyContent: "center",
          }}
          container={() => rootRef.current}
        >
          <Box
            sx={{
              position: "relative",
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: (theme) => theme.shadows[5],
            }}
            style={{ width: "460px", height: "max-content" }}
            className={classes.loginModalWidth}
          >
            <div className={classes.AddMemberHeading}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {props.department ? (ln.edit_department || "Edit Department") : ln.create_department}
              </Typography>
            </div>
            <Button
              variant="text"
              sx={{ minWidth: "unset" }}
              className={classes.closebtn}
              onClick={() => closeModal()}
            >
              <MdClose />
            </Button>
            <ValidatorForm useref="form" onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    htmlFor="address1ControlTextarea"
                    className={`${classes.form_lable}`}
                    sx={{ mb: 1, display: 'block' }}
                  >
                    {ln.department_name}*
                  </Typography>
                  <TextValidator
                    sx={{
                      "& legend": { display: "none" },
                      "& fieldset": { top: 0 },
                    }}
                    id="address1ControlTextarea"
                    value={department}
                    fullWidth
                    onChange={(e) => setDepartment(e.target.value)}
                    size="small"
                    validators={["required"]}
                    errorMessages={["Department Name is required"]}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "13px",
                      width: "100%",
                    }}
                  >
                    <Typography className={`${classes.form_lable}`}>
                      {ln.department_colour}*
                    </Typography>
                    <Typography
                      className={classes.choseOtherColoutTrxt}
                      onClick={() => setOpenColourPalete(true)}
                      sx={{ cursor: 'pointer', color: 'primary.main', fontSize: '0.875rem' }}
                    >
                      Choose other colour
                    </Typography>
                  </Box>
                  {openColourPalete ? (
                    <ChromePicker
                      onChange={handleColour}
                      color={selectedColor}
                      disableAlpha
                    />
                  ) : (
                    <CirclePicker
                      onChange={handleColour}
                      width="100%"
                      color={selectedColor}
                      circleSize={32}
                      colors={availablePalette}
                    />
                  )}
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 4, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => closeModal()}>
                    {ln.cancel}
                  </Button>
                  <Button variant="contained" type="submit">
                    {props.department ? "Update" : ln.add}
                  </Button>
                </Stack>
              </Box>
            </ValidatorForm>
          </Box>
        </Modal>
      ) : null}
      {showAlert && error && (
        <AlertBox type="error" message={error} stateName={setShowAlert} />
      )}
      {showAlert && success && (
        <AlertBox type="success" message={success} stateName={setShowAlert} />
      )}
    </div>
  );
};

export default AddDepartmentModal;
